/**
 * Caret mapping for inline text-edit sessions that hand over between page
 * fragments.
 *
 * A caret is carried as an offset into the session's MARKDOWN, found by
 * serializing the editor doc with a private-use marker character at the caret:
 * the serializer's own escaping, emphasis delimiters and list prefixes then
 * land on the right side of the caret by construction. Restoring it re-parses
 * the destination markdown with the marker spliced in at that offset and reads
 * back which textblock holds the marker and how far into it - a position that
 * survives the markdown-to-doc mapping without re-deriving any of it.
 */

import {
  markdownToEditorDoc,
  editorDocToSliceMarkdown,
  type EditorBlockNode,
  type EditorDoc,
  type EditorHeading,
  type EditorInlineNode,
  type EditorParagraph,
  type FieldResolver,
} from "./textEditModel";

/** A private-use character standing in for the caret during serialization. */
export const CARET_MARKER = "\uE000";

/** A caret as the textblock holding it (document order) and its offset in it. */
export interface TextblockPoint {
  block: number;
  offset: number;
}

/** A live session's caret, read for handing the session to another fragment. */
export interface SessionCaret {
  /** The caret's offset into the editor's serialized markdown. */
  markdownOffset: number;
  /** The caret line's vertical middle, relative to the edit surface's top. */
  top: number;
}

/**
 * Reads the caret offset out of a doc serialized with the caret marker.
 * @param markedDoc - The session doc with the marker inserted at the caret
 * @returns The marker's offset in the serialized markdown, or null without one
 */
export function caretMarkdownOffset(markedDoc: EditorDoc): number | null {
  const offset = editorDocToSliceMarkdown(markedDoc).indexOf(CARET_MARKER);
  return offset === -1 ? null : offset;
}

/**
 * Maps a markdown offset to the textblock point the session doc parsed from
 * that markdown holds it at.
 * @param markdown - The markdown seeding the session
 * @param offset - The caret offset into that markdown, clamped to its bounds
 * @param resolveField - Resolves field sources to chip display values
 * @returns The caret's textblock point, or null when it lands in no textblock
 */
export function caretTextblockPoint(
  markdown: string,
  offset: number,
  resolveField: FieldResolver,
): TextblockPoint | null {
  const at = Math.min(Math.max(offset, 0), markdown.length);
  const marked = markdownToEditorDoc(
    markdown.slice(0, at) + CARET_MARKER + markdown.slice(at),
    resolveField,
  );
  const textblocks = textblocksOf(marked.content);
  const block = textblocks.findIndex((textblock) =>
    (textblock.content ?? []).some(holdsMarker),
  );
  if (block === -1) {
    return null;
  }
  return { block, offset: markerOffset(textblocks[block].content ?? []) };
}

/**
 * Flattens a doc's blocks to its textblocks, in the order ProseMirror visits
 * them.
 * @param blocks - The editor blocks to walk
 * @returns The paragraphs and headings, in document order
 */
function textblocksOf(
  blocks: EditorBlockNode[],
): (EditorParagraph | EditorHeading)[] {
  return blocks.flatMap((block) => {
    if (block.type === "paragraph" || block.type === "heading") {
      return [block];
    }
    if (block.type === "horizontalRule") {
      return [];
    }
    if (block.type === "blockquote") {
      return textblocksOf(block.content);
    }
    return block.content.flatMap((item) => textblocksOf(item.content));
  });
}

/**
 * Whether an inline node carries the caret marker.
 * @param node - The inline node
 * @returns True for a text run holding the marker
 */
function holdsMarker(node: EditorInlineNode): boolean {
  return node.type === "text" && node.text.includes(CARET_MARKER);
}

/**
 * The marker's offset within a textblock, in ProseMirror units: a text run
 * counts its characters, any other inline node counts one.
 * @param content - The textblock's inline content, holding the marker
 * @returns The marker's offset into the textblock
 */
function markerOffset(content: EditorInlineNode[]): number {
  const index = content.findIndex(holdsMarker);
  const before = content
    .slice(0, index)
    .reduce(
      (size, node) => size + (node.type === "text" ? node.text.length : 1),
      0,
    );
  const node = content[index];
  return before + (node.type === "text" ? node.text.indexOf(CARET_MARKER) : 0);
}
